import React from "react";
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import './PageLayout.css';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import Alert from '@mui/material/Alert';
import globals from '../Globals'
import api from '../Auth/Api';
import { subscribePoll } from './Poller';
import { enqueueSnackbar } from "notistack";
import { AttachmentViewerHost } from './Attachments';
import TitlesPage from "../Pages/TitlesPage";
import AdminsPage from "../Pages/AdminsPage";
import DatabasePage from "../Pages/DatabasePage";
import AssetsPage from "../Pages/AssetsPage";
import TestPage from "../Pages/TestPage";
import EventBus from "../EventBus";
import ConfirmLogoutDialog from './ConfirmLogoutDialog';
import AssetInspector from "../Pages/AssetInspector";
import ServersOverviewPage from "../Pages/Servers/OverviewPage";
import MachinesPage from "../Pages/Servers/MachinesPage";
import SessionsPage from "../Pages/Servers/SessionsPage";
import { FleetsPage, SpecsPage } from "../Pages/Servers/FleetsSpecsPages";
import ServersLogsPage from "../Pages/Servers/LogsPage";
import WorldsPage from "../Pages/Servers/WorldsPage";
import ScalingPage from "../Pages/Servers/ScalingPage";
import CostsPage from "../Pages/Servers/CostsPage";
import { RegionsPage, ProvidersPage } from "../Pages/Servers/RegionsProvidersPages";
import VisionPage from "../Pages/Development/VisionPage";
import OverviewPage from "../Pages/Development/OverviewPage";
import CostMachinesPage from "../Pages/Development/CostMachinesPage";
import AgentsPage from "../Pages/Development/AgentsPage";
import MessagesPage from "../Pages/Development/MessagesPage";
import DecisionsPage from "../Pages/Development/DecisionsPage";
import BacklogPage from "../Pages/Development/BacklogPage";
import HitlPage from "../Pages/Development/HitlPage";
import SimulationsPage from "../Pages/Development/SimulationsPage";
import SettingsPage from "../Pages/Development/SettingsPage";
import MemoriesPage from "../Pages/Development/MemoriesPage";
import PurchasesPage from "../Pages/PurchasesPage";
import NewsPage from "../Pages/NewsPage";
import MatchesPage from "../Pages/MatchesPage";
import MigrationPage from "../Pages/MigrationPage";
import SocialPage from "../Pages/SocialPage";
import LocalizationPage from "../Pages/Localization/LocalizationPage";
import SdlbPage from "../Pages/LiveOps/SdlbPage";
import OffersPage from "../Pages/LiveOps/OffersPage";
import EventsPage from "../Pages/LiveOps/EventsPage";
import SystemHealthPage from "../Pages/SystemHealthPage";
import ServerErrorsPage from "../Pages/ServerErrorsPage";
import HealthLatencyPage from "../Pages/HealthLatencyPage";
import HomePage from "../Pages/HomePage";
import PlayersPage from "../Pages/PlayersPage";
import GlobalSettingsPage from "../Pages/GlobalSettingsPage";
import ErrorBoundary from "./ErrorBoundary";
import Divider from '@mui/material/Divider';
import { ROUTE_EVENT, parsePath, buildPath, navigate, replace, translateLegacyHash } from "./Router";

// The page registry: string ids (they are the URL - see Router.js - and survive reordering), object order = menu
// order. Every page/sub carries its `component` (rendered by routePage) and, for one that belongs to a backend
// service, `service: "<id>"` - shown only for titles whose `services` (login data, from the backend's ServiceRegistry)
// include it. `required` gates by roles of the selected title (`anyTitle`: of any scope, for global pages).
export const Page = {
    // Global pages: `menu: true` puts a page in the top Menu; the side bar's only global entry is Game Titles.
    Titles: { id: "titles", title: "Game Titles", global: true, component: () => <TitlesPage key="titlesPage" /> },
    Admins: { id: "admins", title: "Admins", global: true, menu: true, component: () => <AdminsPage key="adminsPage" /> },
    Settings: { id: "settings", title: "Settings", global: true, menu: true, adminOnly: true, component: () => <GlobalSettingsPage key="globalSettingsPage" /> },
    SystemHealth: { id: "health", title: "System Health", global: true, menu: true, service: "health", defaultSub: "overview",
        required: { permissions: ["health"], mode: "any", anyTitle: true }, subs: {
            Overview: { id: "overview", title: "Overview", component: () => <SystemHealthPage key="systemHealthPage" /> },
            Latency: { id: "latency", title: "Latency",
                component: () => <HealthLatencyPage key={"healthLatencyPage-" + (globals.routeNonce ?? 0)} /> },
            Errors: { id: "errors", title: "Errors",
                component: () => <ServerErrorsPage key={"serverErrorsPage-" + (globals.routeNonce ?? 0)} /> },
        }},

    // Title-scoped pages. A page that reads a deep link (a player, a version, an asset) keys on routeNonce, so a new
    // link mounts it fresh; plain menu navigation keeps it mounted.
    Home: { id: "home", title: "Home", required: { permissions: ["players", "iap"], mode: "any" }, subs: {
        Overview: { id: "overview", title: "Overview", required: { permissions: ["players"], mode: "any" },
            component: () => <HomePage key={"homePage-" + globals.selectedTitle} /> },
        Players: { id: "players", title: "Players", required: { permissions: ["players"], mode: "any" },
            component: () => <PlayersPage key={"playersPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
        Purchases: { id: "purchases", title: "Purchases", service: "iap", required: { permissions: ["iap"], mode: "any" },
            component: () => <PurchasesPage key={"purchasesPage-" + globals.selectedTitle} /> },
    }},
    Migration: { id: "migration", title: "Migration", service: "migration", required: { permissions: ["migration"], mode: "any" },
        component: () => <MigrationPage key={"migrationPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
    Matches: { id: "matches", title: "Matches", service: "audit", required: { permissions: ["audit"], mode: "any" },
        component: () => <MatchesPage key={"matchesPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
    Social: { id: "social", title: "Social", service: "social", required: { permissions: ["social"], mode: "any" },
        component: () => <SocialPage key={"socialPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
    Localization: { id: "localization", title: "Localization", service: "localization", required: { permissions: ["localization"], mode: "any" },
        component: () => <LocalizationPage key={"localizationPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
    // Live operations: the `liveops` role (LiveOps service) opens the page; each sub-page belongs to its own service
    // (2026-09-24: News moved in here from the top level - a news-only account still opens the group, seeing the News sub alone)
    LiveOps: { id: "liveops", title: "LiveOps", service: "liveops", defaultSub: "sdlb", required: { permissions: ["liveops", "news"], mode: "any" }, subs: {
        Sdlb: { id: "sdlb", title: "SDLB", service: "sdlb",
            component: () => <SdlbPage key={"sdlbPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
        Offers: { id: "offers", title: "Offers", service: "eviltower-liveops",
            component: () => <OffersPage key={"offersPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
        Events: { id: "events", title: "Events", service: "eviltower-liveops",
            component: () => <EventsPage key={"eventsPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
        // movedFrom: the old top-level path (/<game>/news/…) still resolves here (Router.parsePath)
        News: { id: "news", title: "News", service: "news", movedFrom: "news", required: { permissions: ["news"], mode: "any" },
            component: () => <NewsPage key={"newsPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
    }},
    // Game Servers (Docs/GameServers.md §11-§16): `#servers` views, `servers` acts. movedFrom: the old sub id
    // /<game>/servers/deploy still resolves (Router.parsePath); /servers/scaling is the Scaling page again (2026-10-18)
    Servers: { id: "servers", title: "Game Servers", service: "servers", defaultSub: "overview", subs: {
        Overview: { id: "overview", title: "Overview",
            component: () => <ServersOverviewPage key={"gsOverviewPage-" + globals.selectedTitle} /> },
        Machines: { id: "machines", title: "Machines", movedFrom: "deploy",
            component: () => <MachinesPage key={"gsMachinesPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
        Worlds: { id: "worlds", title: "Worlds",
            component: () => <WorldsPage key={"gsWorldsPage-" + globals.selectedTitle} /> },
        Sessions: { id: "sessions", title: "Sessions",
            component: () => <SessionsPage key={"gsSessionsPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
        Scaling: { id: "scaling", title: "Scaling",
            component: () => <ScalingPage key={"gsScalingPage-" + globals.selectedTitle} /> },
        Costs: { id: "costs", title: "Costs",
            component: () => <CostsPage key={"gsCostsPage-" + globals.selectedTitle} /> },
        Regions: { id: "regions", title: "Regions",
            component: () => <RegionsPage key={"gsRegionsPage-" + globals.selectedTitle} /> },
        Providers: { id: "providers", title: "Providers",
            component: () => <ProvidersPage key={"gsProvidersPage-" + globals.selectedTitle} /> },
        Fleets: { id: "fleets", title: "Fleets",
            component: () => <FleetsPage key={"gsFleetsPage-" + globals.selectedTitle} /> },
        Specs: { id: "specs", title: "Machine Specs",
            component: () => <SpecsPage key={"gsSpecsPage-" + globals.selectedTitle} /> },
        Logs: { id: "logs", title: "Logs",
            component: () => <ServersLogsPage key={"gsLogsPage-" + globals.selectedTitle + "-" + (globals.routeNonce ?? 0)} /> },
    }, required: { permissions: ["servers"], mode: "any"}},
    Database: { id: "database", title: "Database", service: "database", subs: {
        Versioning: { id: "versioning", title: "Versioning",
            required: { permissions: ["database", "servers"], mode: "any"},
            component: () => <DatabasePage key={`databasePage-${globals.selectedTitle}-${globals.routeNonce ?? 0}`} /> },
        Assets: { id: "assets", title: "Asset Manager",
            required: { permissions: ["database"], mode: "any"},
            component: () => <AssetsPage key={`assetsPage-${globals.selectedTitle}-${globals.routeNonce ?? 0}`} /> },
        AssetInspector: { id: "inspector", title: "Asset Inspector",
            required: { permissions: ["database"], mode: "any"},
            component: () => <AssetInspector key={`assetInspector-${globals.selectedTitle}-${globals.routeNonce ?? 0}`} /> },
    }},
    Test: { id: "test", title: "Test", component: () => <TestPage key="testPage" /> },

    // AI development (title-scoped)
    Development: { id: "development", title: "Development", service: "aidev", defaultSub: "overview",
        required: { permissions: ["orchestrator", "development"], mode: "any" },
        subs: {
            Overview: { id: "overview", title: "Overview", component: () => <OverviewPage key="overviewPage" /> },
            Vision: { id: "vision", title: "Vision", component: () => <VisionPage key="visionPage" /> },
            Memories: { id: "memories", title: "Memories", component: () => <MemoriesPage key="memoriesPage" /> },
            Agents: { id: "agents", title: "Agents", component: () => <AgentsPage key="agentsPage" /> },
            Messages: { id: "messages", title: "Messages", component: () => <MessagesPage key="messagesPage" /> },
            Decisions: { id: "decisions", title: "Decisions", component: () => <DecisionsPage key="decisionsPage" /> },
            Backlog: { id: "backlog", title: "Backlog", component: () => <BacklogPage key="backlogPage" /> },
            Hitl: { id: "hitl", title: "Human Review", component: () => <HitlPage key="hitlPage" /> },
            CostMachines: { id: "cost", title: "Cost & Machines", component: () => <CostMachinesPage key="costMachinesPage" /> },
            Simulations:  { id: "simulations", title: "Simulations", component: () => <SimulationsPage key="simulationsPage" /> },
            Settings:     { id: "settings", title: "Settings", required: { permissions: ["orchestrator"], mode: "any" },
                component: () => <SettingsPage key="settingsPage" /> },
        }
    }
}

const LOGOUT = "logout";   // the Menu's last entry; not a page

export default class PageLayout extends React.Component {
    state = {
        currentPage: Page.Titles.id,
        currentSub: null,
        menuOpen: true,
        confirmLogoutOpen: false,
        selectedTitleVersion: 0,
        dropdownAnchorEl: null,
        inbox: null,        // Development inbox summary (badge counts), polled every 30 s while an AiDev title is selected
    }

    // ----- Development badges: one `inbox-summary` poll for the whole site -----
    canPollInbox = () => !!globals.selectedTitle && this.isPageVisible(Page.Development);
    loadInbox = async () => {
        const title = globals.selectedTitle;
        this.inboxTitle = title;
        if (!this.canPollInbox()) { if (this.state.inbox) this.setState({ inbox: null }); return true; }
        try {
            const r = await api.development("Loading inbox summary", ["inbox-summary"], { silent: true });
            if (r && typeof r === "object" && globals.selectedTitle === title) this.setState({ inbox: r });
            return true;
        } catch { return false; }
    };
    // Badge count per Development sub-page (Human Review: open questions + proposed decisions; Messages: unread).
    subBadge = (page, sub) => {
        const i = this.state.inbox;
        if (!i || page.id !== Page.Development.id) return 0;
        if (sub.id === Page.Development.subs.Hitl.id) return (i.pendingQuestions || 0) + (i.pendingDecisions || 0);
        if (sub.id === Page.Development.subs.Messages.id) return i.unreadTotal || 0;
        return 0;
    };
    pageBadge = (page) => Object.values(page.subs || {}).reduce((n, sub) => n + this.subBadge(page, sub), 0);
    badged = (label, count) => count > 0
        ? <Badge badgeContent={count} color="error" max={99} sx={{ "& .MuiBadge-badge": { right: -14, top: 1, height: 16, minWidth: 16, fontSize: 10, px: 0.5 } }}><span>{label}</span></Badge>
        : <span>{label}</span>;

    closeConfirmDialog = () => {
        this.setState({ confirmLogoutOpen: false });
    }

    handleLogout = () => {
        this.setState({ confirmLogoutOpen: false }, () => {
            EventBus.emit("logout", false);
        });
    }

    handleForgetAndLogout = () => {
        this.setState({ confirmLogoutOpen: false }, () => {
            EventBus.emit("logout", true);
        });
    }

    toggleMenu = () => {
        this.setState({ menuOpen: !this.state.menuOpen });
    }

    openDropdown = (e) => {
        this.setState({ dropdownAnchorEl: e.currentTarget });
    }

    closeDropdown = () => {
        this.setState({ dropdownAnchorEl: null });
    }

    dropdownItemClicked = (id, subId = null) => {
        this.closeDropdown();
        if (id === LOGOUT) { this.setState({ confirmLogoutOpen: true }); return; }
        this.buttonClicked(id, subId);
    }

    // ----- Registry helpers -----
    pageById = (id) => Object.values(Page).find(p => p.id === id);
    subById = (page, id) => Object.values(page?.subs ?? {}).find(s => s.id === id);
    isTitleScopedPage = (pageId) => { const page = this.pageById(pageId); return page ? !page.global : true; };
    // The sub-page a page opens on: its declared default when visible, else the first visible one, else none.
    defaultSubOf = (page) => {
        const subs = Object.values(page.subs ?? {}).filter(sub => this.isSubVisible(page, sub));
        if (subs.length === 0) return null;
        return (page.defaultSub && subs.some(s => s.id === page.defaultSub)) ? page.defaultSub : subs[0].id;
    }
    routeFor = (pageId, subId) => {
        const page = this.pageById(pageId);
        return { title: page?.global ? null : globals.selectedTitle, page: pageId, sub: subId ?? null, deep: [] };
    }

    // A menu click: one history entry, then the same handler the browser's back/forward and a pasted link go through.
    buttonClicked = (pageId, subId) => {
        if (this.isTitleScopedPage(pageId) && !this.ensureSelectedTitle()) {
            this.redirectToTitlesPage();
            return;
        }
        if (!this.ensurePagePermission(pageId, subId)) return;
        navigate(this.routeFor(pageId, subId));
    }

    getSelectedTitleRoles = () => {
        const titleId = globals.selectedTitle;
        const rolesByTitle = globals.loginData?.titles?.roles;
        if (!titleId || !rolesByTitle) return new Set();

        const raw = rolesByTitle[titleId];

        // Backend may serialize HashSet as array, or as object-map. Support both.
        if (Array.isArray(raw)) return new Set(raw);
        if (raw && typeof raw === "object") return new Set(Object.keys(raw));

        return new Set();
    }

    // Union of the account's roles across ALL titles — for global pages (required.anyTitle).
    getAnyTitleRoles = () => {
        const rolesByTitle = globals.loginData?.titles?.roles;
        const union = new Set();
        if (!rolesByTitle) return union;

        for (const raw of Object.values(rolesByTitle)) {
            if (Array.isArray(raw)) raw.forEach(r => union.add(r));
            else if (raw && typeof raw === "object") Object.keys(raw).forEach(r => union.add(r));
        }
        return union;
    }

    // Does the selected title have this backend service (titles[id].services from the login data)? Global services
    // (System Health, Scheduler) belong to no title: a global page's `service` only documents the owner.
    selectedTitleHasService = (service) => {
        const titleId = globals.selectedTitle;
        const services = globals.loginData?.titles?.titles?.[titleId]?.services ?? [];
        return !!titleId && services.includes(service);
    }

    // A service the selected title implements but cannot use because a service it requires is missing there
    // (titles[id].disabledServices, from the backend's ServiceRegistry): { id, missing: [service ids] }, or null. Its
    // page stays in the menu and shows the reason instead of the feature.
    selectedTitleDisabledService = (service) => {
        const titleId = globals.selectedTitle;
        const list = globals.loginData?.titles?.titles?.[titleId]?.disabledServices ?? [];
        return (!!titleId && list.find(d => d.id === service)) || null;
    }

    hasRequiredPermissions = (required) => {
        // No requirements => always allowed
        if (!required || !required.permissions || required.permissions.length === 0) return true;
        // A master holds every role the backend offers (safety net for a stale cached login)
        if (globals.loginData?.titles?.isMaster) return true;

        const roles = required.anyTitle ? this.getAnyTitleRoles() : this.getSelectedTitleRoles();
        const mode = required.mode ?? "any"; // "any" or "all"

        const hasPerm = (perm) => roles.has(perm) || roles.has(`#${perm}`);

        if (mode === "all") {
            return required.permissions.every(hasPerm);
        }

        // default any
        return required.permissions.some(hasPerm);
    }

    // A sub-page of a service (Home → Purchases) shows only for titles that have it; then its roles, if any.
    isSubVisible = (page, sub) => {
        if (!sub) return true;
        // a disabled service's sub-page shows to every admin of the title: it only says why it is disabled
        if (sub.service && !this.selectedTitleHasService(sub.service)) return !!this.selectedTitleDisabledService(sub.service);
        if (!sub.required) return true;
        return this.hasRequiredPermissions(sub.required);
    }

    isPageVisible = (page) => {
        // A title-scoped page of a service hides unless the selected title has that service - or has it disabled
        // (then the page shows the reason, to every admin of the title)
        if (page.service && !page.global && !this.selectedTitleHasService(page.service)) return !!this.selectedTitleDisabledService(page.service);

        // If page has explicit required, enforce it
        if (page.required && !this.hasRequiredPermissions(page.required)) return false;

        // A page with sub-pages needs at least one the account can open
        const subs = page.subs ? Object.values(page.subs) : [];
        if (subs.length === 0) return true;
        return subs.some(sub => this.isSubVisible(page, sub));
    }

    ensurePagePermission = (pageId, subId) => {
        const page = this.pageById(pageId);
        if (!page) return true;

        // Global pages: if required is set, check it; otherwise allow
        if (!this.isTitleScopedPage(pageId)) {
            if (page.required && !this.hasRequiredPermissions(page.required)) {
                return false;
            }
            return true;
        }

        // Title-scoped pages require selectedTitle and permission visibility
        if (!globals.selectedTitle) return false;

        if (!this.isPageVisible(page)) return false;

        // If navigating to a sub, ensure the sub is visible
        const sub = this.subById(page, subId);
        if (sub && !this.isSubVisible(page, sub)) return false;

        return true;
    }

    // allowStateUpdate=false when called during render (setState is illegal there;
    // the current render already sees the updated globals.selectedTitle)
    ensureSelectedTitle = (allowStateUpdate = true) => {
        const titlesDict = globals.loginData?.titles?.titles ?? {};
        const titleIds = Object.keys(titlesDict);

        const bumpTitleVersion = () => {
            if (allowStateUpdate) {
                this.setState({ selectedTitleVersion: this.state.selectedTitleVersion + 1 });
            }
        };

        // If already selected, ensure it is still valid
        if (globals.selectedTitle && titleIds.includes(globals.selectedTitle)) {
            return true;
        }

        // Try restore from persistent storage
        const stored = localStorage.getItem("selectedTitle");
        if (stored && titleIds.includes(stored)) {
            globals.selectedTitle = stored;
            bumpTitleVersion();
            return true;
        }

        // If there is exactly one possible title, auto-select it
        if (titleIds.length === 1) {
            globals.selectedTitle = titleIds[0];
            localStorage.setItem("selectedTitle", globals.selectedTitle);
            bumpTitleVersion();
            return true;
        }

        // No valid selection
        globals.selectedTitle = null;
        localStorage.removeItem("selectedTitle");
        bumpTitleVersion();
        return false;
    }

    // Show a route this component already validated: the URL rewritten in place (no history entry) and the state.
    show = (route) => {
        replace(route);
        globals.route = route;
        this.setState({ currentPage: route.page, currentSub: route.sub, selectedTitleVersion: this.state.selectedTitleVersion + 1 });
    }

    redirectToTitlesPage = () => {
        this.show({ title: null, page: Page.Titles.id, sub: null, deep: [] });
    }

    // Where a title opens: Home → Overview when the account may see it, else the first page it may; else Titles.
    titleLanding = () => {
        const home = Page.Home;
        if (this.isPageVisible(home) && this.isSubVisible(home, home.subs.Overview)) return this.routeFor(home.id, home.subs.Overview.id);
        for (const page of Object.values(Page)) {
            if (page.global || !this.isPageVisible(page)) continue;
            return this.routeFor(page.id, this.defaultSubOf(page));
        }
        return null;
    }

    // The location -> the page. Runs on mount, on back/forward, and after every navigate(): a title in the URL
    // selects it (mailed and copied links carry one), a page without sub named opens on its default, a legacy hash
    // is translated first, and anything the account may not open lands on the title's home or the Titles page.
    applyLocation = () => {
        const stored = globals.selectedTitle || localStorage.getItem("selectedTitle");
        const legacy = translateLegacyHash(window.location.hash, stored);
        if (legacy) window.history.replaceState(null, "", buildPath(legacy));
        const route = legacy ?? parsePath(window.location.pathname, Page);

        if (route?.title) {
            const titles = globals.loginData?.titles?.titles ?? {};
            if (!titles[route.title]) {
                enqueueSnackbar(`Unknown game '${route.title}'.`, { variant: "error" });
                this.redirectToTitlesPage();
                return;
            }
            if (globals.selectedTitle !== route.title) {
                globals.selectedTitle = route.title;
                localStorage.setItem("selectedTitle", route.title);
            }
        }

        // The root, or a game with no page: the title's landing page (or Titles when there is no title to open)
        if (!route || !route.page) {
            if (!this.ensureSelectedTitle(false)) { this.redirectToTitlesPage(); return; }
            const landing = this.titleLanding();
            if (!landing) { this.redirectToTitlesPage(); return; }
            this.show(landing);
            return;
        }

        const page = this.pageById(route.page);
        if (!page.global && !route.title) {
            // "/home" without a game: the selected one
            if (!this.ensureSelectedTitle(false)) { this.redirectToTitlesPage(); return; }
            route.title = globals.selectedTitle;
        }
        if (!route.sub && page.subs && Object.keys(page.subs).length > 0) route.sub = this.defaultSubOf(page);

        if (!this.ensurePagePermission(page.id, route.sub)) {
            const landing = page.global ? null : this.titleLanding();
            if (landing) this.show(landing); else this.redirectToTitlesPage();
            return;
        }

        // A deep link mounts its page fresh (the page keys on routeNonce); plain navigation keeps it mounted.
        if (route.deep.length > 0) globals.routeNonce = (globals.routeNonce ?? 0) + 1;
        this.show(route);
    }

    generateButtons(){
        let buttons = [];
        let prevRendered = null;

        for (const page of Object.values(Page)) {
            // Global pages of the top Menu are not in the side bar
            if (page.menu) continue;
            // Hide title-scoped pages until a title is selected
            if (this.isTitleScopedPage(page.id) && !globals.selectedTitle) {
                continue;
            }

            // Hide pages the user cannot access
            if (!this.isPageVisible(page)) {
                continue;
            }

            let active = this.state.currentPage === page.id;

            let classes = "side-menu-button";
            if (active) classes += " side-menu-button-current";

            // Separator between the global section and the title-scoped section
            if (prevRendered && prevRendered.global && !page.global) {
                buttons.push(<div key="menu-separator-global" className="side-menu-separator" />);
            }
            prevRendered = page;

            // A collapsed section shows the sum of its sub-page badges; an open one shows them per sub-page.
            buttons.push(
                <div key={page.id} className={classes} onClick={() => this.buttonClicked(page.id, this.defaultSubOf(page))}>
                    <div>{this.badged(page.title, active ? 0 : this.pageBadge(page))}</div>
                </div>
            );

            if (active) {
                for (const sub of Object.values(page.subs ?? {})) {
                    if (!this.isSubVisible(page, sub)) continue;

                    let key = page.id + "-" + sub.id;

                    let subClasses = "side-menu-button side-submenu-button";
                    if (this.state.currentSub === sub.id) subClasses += " side-menu-button-current";

                    buttons.push(
                        <div key={key} className={subClasses} onClick={() => this.buttonClicked(page.id, sub.id)}>
                            <div>{this.badged(sub.title, this.subBadge(page, sub))}</div>
                        </div>
                    );
                }
            }
        }

        return buttons;
    }

    generateMenu = () => {
        if (this.state.menuOpen) {
            return (
                <div id="side-menu">
                    {this.generateButtons()}
                </div>
            );
        } else {
            return (<></>);
        }
    }

    // The top Menu: every global page the account may open (Admins, Settings, System Health with its sub-pages), then Logout.
    menuPages = () => Object.values(Page).filter(page => page.menu && this.isPageVisible(page)
        && (!page.adminOnly || globals.loginData?.titles?.hasAdminRole || globals.loginData?.titles?.isMaster));

    routePage() {
        // Defensive: never render scoped pages without a selected title
        if (this.isTitleScopedPage(this.state.currentPage)) {
            const ok = this.ensureSelectedTitle(false);
            if (!ok) {
                return <TitlesPage key="titlesPage" />;
            }
        }

        // Registry lookup: the sub's component, else the page's; the Development section also shows the Librarian notice.
        const page = this.pageById(this.state.currentPage);
        if (!page) return <h2>Empty Page</h2>;
        const sub = this.subById(page, this.state.currentSub);
        // the page of a service the title has but cannot use: the reason, not the feature
        const service = sub?.service ?? (!page.global ? page.service : null);
        const disabled = service ? this.selectedTitleDisabledService(service) : null;
        if (disabled) return this.serviceDisabledNotice(disabled);
        const render = sub?.component ?? page.component;
        const content = render ? render() : <h2>{page.title}: Empty Page</h2>;
        if (page.id === Page.Development.id) return <>{this.librarianBanner()}{content}</>;
        return content;
    }
    // What a disabled service's page shows: which required service the title lacks (the backend keeps the whole
    // service off for the title until its GameTitle implements that service too).
    serviceDisabledNotice(disabled) {
        const registry = globals.loginData?.titles?.rolesDefinition?.services ?? [];
        const name = (id) => registry.find(s => s.id === id)?.title ?? id;
        const info = globals.loginData?.titles?.titles?.[globals.selectedTitle];
        const titleName = info?.title ?? info?.name ?? globals.selectedTitle;
        const missing = (disabled.missing ?? []).map(name);
        return <Alert severity="warning" sx={{ m: 2 }}>
            <strong>{name(disabled.id)} is disabled on {titleName}.</strong> Required {missing.length === 1 ? "dependency" : "dependencies"} {missing.join(" and ")} {missing.length === 1 ? "is" : "are"} not
            installed on this game title. {name(disabled.id)} works here once the title implements {missing.join(" and ")} as well (its marker interface in the backend) and a new version deploys.
        </Alert>;
    }

    // Site-wide notice: while a Librarian pass is running or pending, project agents cannot start (mutual exclusion enforced by the backend).
    librarianBanner() {
        const l = this.state.inbox && this.state.inbox.librarian;
        if (!l || !(l.running || l.pending)) return null;
        const since = l.since ? new Date(l.since).toLocaleString() : "";
        return <Alert severity="info" sx={{ mb: 2 }}>
            {l.running ? `Librarian pass running${since ? ` since ${since}` : ""}` : `Librarian pass pending${since ? ` since ${since}` : ""}${l.reason ? ` (${l.reason})` : ""}, retried every 10 minutes`} — project agents cannot start until it finishes; briefings, messages and tasks are stored and the agents start automatically afterwards.
        </Alert>;
    }
    render() {
        return (
            <>
                <div id="top-menu">
                    <IconButton
                        id="top-menu-button"
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        style={{ marginRight: '12px' }}
                        onClick={this.toggleMenu}
                    >
                        <MenuIcon />
                    </IconButton>
                    <span id="top-menu-title">
                        {globals.selectedTitle
                            ? (globals.loginData?.titles?.titles?.[globals.selectedTitle]?.title
                                ?? globals.loginData?.titles?.titles?.[globals.selectedTitle]?.name
                                ?? globals.selectedTitle)
                            : "Aurecas"}
                    </span>
                    <span id="top-menu-logout" onClick={this.openDropdown}>
                        Menu
                    </span>
                    <Menu
                        anchorEl={this.state.dropdownAnchorEl}
                        open={Boolean(this.state.dropdownAnchorEl)}
                        onClose={this.closeDropdown}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {this.menuPages().flatMap((page) => [
                            <MenuItem key={page.id} selected={this.state.currentPage === page.id && !page.subs} onClick={() => this.dropdownItemClicked(page.id, this.defaultSubOf(page))}>
                                {page.title}
                            </MenuItem>,
                            // a menu page's sub-pages sit indented right under it (System Health → Overview, Errors)
                            ...Object.values(page.subs ?? {}).filter(sub => this.isSubVisible(page, sub)).map(sub => (
                                <MenuItem key={page.id + "-" + sub.id} dense sx={{ pl: 4 }}
                                          selected={this.state.currentPage === page.id && this.state.currentSub === sub.id}
                                          onClick={() => this.dropdownItemClicked(page.id, sub.id)}>
                                    {sub.title}
                                </MenuItem>
                            )),
                        ])}
                        <Divider />
                        <MenuItem key={LOGOUT} onClick={() => this.dropdownItemClicked(LOGOUT)}>Logout</MenuItem>
                    </Menu>
                    <span id="top-menu-email">{globals.loginData.name}</span>
                </div>
                {this.generateMenu()}
                <div
                    id="page-content"
                    align="center"
                    className={this.state.menuOpen ? "page-content-margin" : ""}
                >
                    <AttachmentViewerHost />
                    <ErrorBoundary key={`${this.state.currentPage}-${this.state.currentSub}`}>{this.routePage()}</ErrorBoundary>
                </div>
                <ConfirmLogoutDialog
                    open={this.state.confirmLogoutOpen}
                    onClose={this.closeConfirmDialog}
                    onLogout={this.handleLogout}
                    onForgetAndLogout={this.handleForgetAndLogout}
                />
            </>
        );
    }

    componentDidMount() {
        window.addEventListener("popstate", this.applyLocation);
        EventBus.on(ROUTE_EVENT, this.applyLocation);

        // If title is not set: try restore, otherwise auto-select if only one title.
        if (!globals.selectedTitle) {
            this.ensureSelectedTitle();
        }

        this.applyLocation();

        // Badge counts: one poll for the whole site; pages that change the inbox emit "aidev-inbox" for an immediate refresh.
        this.unsubInbox = subscribePoll(this.loadInbox, 30000);
        EventBus.on("aidev-inbox", this.loadInbox);
        this.loadInbox();
    }

    componentDidUpdate() {
        // Title switched (Titles page) -> the badges belong to the new title.
        if (globals.selectedTitle !== this.inboxTitle) this.loadInbox();
    }

    componentWillUnmount() {
        window.removeEventListener("popstate", this.applyLocation);
        EventBus.off(ROUTE_EVENT, this.applyLocation);
        if (this.unsubInbox) this.unsubInbox();
        EventBus.off("aidev-inbox", this.loadInbox);
    }
}
